import React, { useEffect, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, { PaginationProvider }  from "react-bootstrap-table2-paginator";
import { downloadErrorAlert, getAuthorities, saveAsFile } from "../../../../../../_helpers/Helpers";
import * as actions from "../../../_redux/public-notices/PublicNoticesAction";
import * as uiHelpers from "../PublicNoticesUIHelpers";
import { 
  getSelectRow, 
  getHandlerTableChange,  
  NoRecordsFoundMessage, 
  PleaseWaitMessage, 
  sortCaret
} from "../../../../../../_metronic/_helpers";
import * as columnFormatters from "./column-formatters";
import { Pagination } from "../../../../../../_metronic/_partials/controls";
import { usePublicNoticesUIContext } from "../PublicNoticesUIContext";

export function PublicNoticesTable({ 
  deletePublicNotices
}) {
  // PublicNotices UI Context
  const publicNoticesUIContext = usePublicNoticesUIContext();
  const publicNoticesUIProps = useMemo(() => {
    return {
      ids: publicNoticesUIContext.ids,
      setIds: publicNoticesUIContext.setIds,
      queryParams: publicNoticesUIContext.queryParams,
      setQueryParams: publicNoticesUIContext.setQueryParams,
      openPublicNoticeDetailPage: publicNoticesUIContext.openPublicNoticeDetailPage
    };
  }, [publicNoticesUIContext]);

  // Getting curret state of PublicNotices list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.publicNotices }),
    shallowEqual
  );
  const { totalCount, entities, authorities, listLoading } = currentState;
  
  // Authority Parameters
  const { isDeletionAuthorized } = getAuthorities(authorities);
  
  // Public Notices Redux state
  const dispatch = useDispatch();

  useEffect(() => {
    // clear selections list
    publicNoticesUIProps.setIds([]);
    dispatch(actions.getPublicNotices(publicNoticesUIProps.queryParams));
    return () => dispatch(actions.cleanPublicNoticesState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicNoticesUIProps.queryParams, dispatch]);

  /** Belli Bir Eki İndirir */
  const downloadAttachment = (id, filename) => {
    dispatch(actions.downloadAttachment(id)).then(file => {
      if (!file) {
        downloadErrorAlert();
        return;
      }
      saveAsFile(file, filename);
  })}

  // Table columns
  const columns = [
    {
      dataField: "sender",
      text: "Gönderen",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "150px"
      }
    },
    {
      dataField: "definition",
      text: "Tanımı",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "200px"
      }
    },
    {
      dataField: "explanation",
      text: "Açıklaması",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "200px"
      }
    },
    {
      dataField: "createDate",
      text: "Tarih",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "100px"
      }
    },
    {
      dataField: "attachmentName",
      text: "Ek",
      sort: true,
      sortCaret: sortCaret,
      formatter: (cell, row) => (
        <a href="javascript:" onClick={() => downloadAttachment(row.id, row.attachmentName)}> {cell}</a>
      ),
      style: {
        minWidth: "150px"
      }
    },
    {
      dataField: "isSent",
      text: "Durum",
      sort: true,
      sortCaret: sortCaret,
      formatter: columnFormatters.StatusColumnFormatter,
      style: {
        minWidth: "100px"
      }
    },
    {
      dataField: "action",
      text: "İşlemler",
      formatter: columnFormatters.ActionsColumnFormatter,
      formatExtraData: {
        isDeletionAuthorized,
        openPublicNoticeDetailPage: publicNoticesUIProps.openPublicNoticeDetailPage,
        deletePublicNotices
      },
      classes: "text-right pr-0",
      headerClasses: "text-right pr-3",
      style: {
        minWidth: "100px",
      }
    }
  ];

  // Table pagination properties
  const paginationOptions = {
    custom: true,
    totalSize: totalCount,
    sizePerPageList: uiHelpers.sizePerPageList,
    sizePerPage: publicNoticesUIProps.queryParams.pageSize,
    page: publicNoticesUIProps.queryParams.pageNumber
  };
  
  return (
    <PaginationProvider pagination={paginationFactory(paginationOptions)}>
      {({ paginationProps, paginationTableProps }) => {
        return (
          <Pagination
            isLoading={listLoading}
            paginationProps={paginationProps}
          >
            <BootstrapTable
              wrapperClasses="table-responsive"
              classes="table table-head-custom table-vertical-center overflow-hidden"
              bootstrap4
              bordered={false}
              remote
              keyField="id"
              data={entities ? entities : [] }
              columns={columns}
              defaultSorted={uiHelpers.defaultSorted}
              onTableChange={getHandlerTableChange(
                publicNoticesUIProps.setQueryParams
              )}
              selectRow={getSelectRow({
                entities,
                ids: publicNoticesUIProps.ids,
                setIds: publicNoticesUIProps.setIds,
                hidden: !isDeletionAuthorized
              })}
              {...paginationTableProps}
            >
              <PleaseWaitMessage entities={entities} />
              <NoRecordsFoundMessage entities={entities} />
            </BootstrapTable>
          </Pagination>
        );    
      }}
    </PaginationProvider>  
  );
}